import React, { useEffect, useRef, useState } from "react";
import { api, formatError } from "@/lib/api";
import { toast } from "sonner";
import { UserPlus, Trash2, ShieldCheck, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const MAX_ADDITIONAL = 4;

export default function AdminAdmins() {
  const { user } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/admins").then(({ data }) => setAdmins(data));
  useEffect(() => { load(); }, []);

  const isPrimary = admins.find((a) => a.email === user?.email)?.is_primary;
  const additionalCount = admins.filter((a) => !a.is_primary).length;
  const remaining = MAX_ADDITIONAL - additionalCount;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/admins", form);
      toast.success("Admin created");
      setForm({ name: "", email: "", password: "" });
      setShowForm(false);
      load();
    } catch (err) { toast.error(formatError(err)); } finally { setSaving(false); }
  };

  const del = async (u) => {
    if (!window.confirm(`Remove ${u.name} (${u.email}) from admins?`)) return;
    try { await api.delete(`/admin/admins/${u.user_id}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-admins-page">
      <div>
        <div className="overline mb-2">Access Management</div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-ink-900">Admins</h1>
        <p className="text-ink-600 text-sm mt-2">Primary admin can add up to {MAX_ADDITIONAL} additional admins. Additional admins have full access but cannot manage other admins.</p>
      </div>

      <div className="glass rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-ink-900/[0.04]">
            <tr className="text-left text-ink-600"><th className="p-3">Name</th><th>Email</th><th>Type</th><th>Since</th><th></th></tr>
          </thead>
          <tbody>
            {admins.map((a) => (
              <tr key={a.user_id} className="border-t border-ink-900/10" data-testid={`admin-row-${a.user_id}`}>
                <td className="p-3 text-ink-900 font-medium">{a.name}</td>
                <td className="text-ink-700">{a.email}</td>
                <td>
                  {a.is_primary ? (
                    <span className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-gold" data-testid={`admin-primary-${a.user_id}`}>
                      <ShieldCheck className="w-3.5 h-3.5" /> Primary
                    </span>
                  ) : (
                    <span className="text-xs uppercase tracking-wider text-ink-500">Admin</span>
                  )}
                </td>
                <td className="text-ink-500 text-xs">{a.created_at ? new Date(a.created_at).toLocaleDateString() : "—"}</td>
                <td className="p-3">
                  {!a.is_primary && isPrimary && (
                    <button onClick={() => del(a)} className="text-red-600 hover:text-red-700" data-testid={`admin-delete-${a.user_id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {isPrimary ? (
        <div className="flex items-center justify-between">
          <div className="text-sm text-ink-600">{remaining > 0 ? `${remaining} additional admin slot${remaining === 1 ? "" : "s"} available` : "All additional admin slots used."}</div>
          <button onClick={() => setShowForm(true)} disabled={remaining <= 0} className="btn-neon disabled:opacity-40" data-testid="admin-add-btn">
            <UserPlus className="w-4 h-4 mr-2" /> Add admin
          </button>
        </div>
      ) : (
        <p className="text-sm text-ink-500">Only the primary admin can add or remove admins.</p>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-ink-900/40" onClick={() => setShowForm(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="glass-strong rounded-2xl p-6 w-full max-w-md space-y-3" data-testid="admin-add-form">
            <div className="flex justify-between items-center">
              <h3 className="font-display text-xl text-ink-900">Add admin</h3>
              <button type="button" onClick={() => setShowForm(false)}><X /></button>
            </div>
            <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="admin-add-name" />
            <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="admin-add-email" />
            <Field label="Temporary password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="admin-add-password" />
            <button type="submit" disabled={saving} className="btn-neon w-full" data-testid="admin-add-submit">{saving ? "Creating..." : "Create admin"}</button>
          </form>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", testid }) => (
  <label className="block">
    <div className="overline mb-2 !text-ink-600">{label}</div>
    <input required type={type} value={value} onChange={(e) => onChange(e.target.value)}
      className="w-full rounded-lg bg-cream-50 border border-ink-900/15 px-4 py-3 outline-none focus:border-gold text-ink-900 text-sm" data-testid={testid} />
  </label>
);
